import { type Dispatch, useCallback, useEffect, useRef } from "react";
import {
  toBillingCommandErrorMessage,
  type WebsocketData,
} from "@intelligems/ig-types";

import { useHandleSelfServeCommand } from "@src/api/billing-workflow/self-serve-commands";
import type {
  BillingCommandAnalyticsEvent,
  BillingCommandFeedback,
  PlannedBillingCommand,
} from "./commandTypes";
import { isOptimisticEventConfirmed } from "./optimistic";
import { isTerminalCommandFailure } from "./reconciliation";
import type { BillingSnapshot } from "./types";
import type {
  BillingWorkflowAction,
  BillingWorkflowCommandState,
} from "./workflowReducer";

type ActiveExecution = {
  // The approval a `recorded` push said this change cannot apply without. While
  // set, the execution waits for that approval's link, whatever the snapshot
  // shows: the plan intent is visible before the link is.
  awaitedShopifyApprovalId: string | null;
  commandId: string | null;
  leftForShopifyApproval: boolean;
  plan: PlannedBillingCommand;
};

type TimerRef = { current: ReturnType<typeof setTimeout> | null };

const clearTimer = (ref: TimerRef): void => {
  if (ref.current === null) {
    return;
  }
  clearTimeout(ref.current);
  ref.current = null;
};

// How long a completed command may wait for its optimistic overlay to be
// confirmed by a fresh snapshot before the workflow settles anyway. Bounds the
// stuck-forever case where the confirming refetch or websocket message is
// lost: the overlay tears down and the single-flight lock releases, with no
// error feedback because nothing failed.
const SERVER_CONFIRM_DEADLINE_MS = 15_000;

// Said as the merchant leaves for Shopify, never before: until the backend
// resolves the disposition, "you are going to Shopify" is a promise nobody can
// keep. The dialog's building-plan card explains the wait until then.
const SHOPIFY_APPROVAL_REDIRECT_FEEDBACK: BillingCommandFeedback = {
  message: "Sending you to Shopify to approve this change.",
  tone: "info",
  confetti: false,
};

type BillingCommandExecutorParams = Readonly<{
  command: BillingWorkflowCommandState;
  dispatch: Dispatch<BillingWorkflowAction>;
  emitAnalytics: (event: BillingCommandAnalyticsEvent) => void;
  emitFeedback: (feedback: BillingCommandFeedback) => void;
  invalidateSubscriptionAccess: () => void | Promise<void>;
  isRawSnapshotFetching: boolean;
  // Returns true when the caller fully handles a coded terminal failure
  // (e.g. escalates it to a dialog) — the executor then skips its toast but
  // still emits error analytics and releases the lock.
  onTerminalFailure: (errorCode: string) => boolean;
  orgId: string;
  rawSnapshot: BillingSnapshot | null;
  redirect: (url: string) => void;
  websocketMessage: WebsocketData | null;
}>;

export const useBillingCommandExecutor = ({
  command,
  dispatch,
  emitAnalytics,
  emitFeedback,
  invalidateSubscriptionAccess,
  isRawSnapshotFetching,
  onTerminalFailure,
  orgId,
  rawSnapshot,
  redirect,
  websocketMessage,
}: BillingCommandExecutorParams): Readonly<{
  execute: (plan: PlannedBillingCommand) => Promise<void>;
}> => {
  const mutation = useHandleSelfServeCommand();
  const activeRef = useRef<ActiveExecution | null>(null);
  const invalidateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const approvalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handledMessageRef = useRef<WebsocketData | null>(null);
  const previousFetchingRef = useRef(isRawSnapshotFetching);
  const executionOrgIdRef = useRef(orgId);
  const reducerCommandId = command.status === "idle" ? null : command.commandId;

  const releaseExecution = useCallback(() => {
    activeRef.current = null;
    clearTimer(invalidateTimerRef);
    clearTimer(approvalTimerRef);
    clearTimer(settleTimerRef);
  }, []);

  // The confirmation URL is the backend's answer that this change is gated by a
  // Blocking Approval, so the handoff is announced here and nowhere else.
  const leaveForShopifyApproval = useCallback(
    (execution: ActiveExecution, approvalUrl: string) => {
      // More than one push can carry the link — the recorded plan and the
      // command that completes it — but the merchant leaves exactly once.
      if (execution.leftForShopifyApproval) {
        return;
      }
      clearTimer(approvalTimerRef);
      execution.leftForShopifyApproval = true;
      for (const event of execution.plan.approvalAnalytics) {
        emitAnalytics(event);
      }
      emitFeedback(SHOPIFY_APPROVAL_REDIRECT_FEEDBACK);
      redirect(approvalUrl);
    },
    [emitAnalytics, emitFeedback, redirect]
  );

  const emitAllAnalytics = useCallback(
    (events: readonly BillingCommandAnalyticsEvent[]) => {
      for (const event of events) {
        emitAnalytics(event);
      }
    },
    [emitAnalytics]
  );

  const failExecution = useCallback(
    (plan: PlannedBillingCommand, errorCode?: string) => {
      emitAllAnalytics(plan.analytics.onError);
      if (errorCode && onTerminalFailure(errorCode)) {
        releaseExecution();
        return;
      }
      if (errorCode === "policy_rejected" && plan.feedback.policyRejected) {
        dispatch({
          type: "show-command-blocked",
          ...plan.feedback.policyRejected,
        });
        releaseExecution();
        return;
      }
      // A coded terminal failure substitutes only the message: the catalog
      // copy is more specific than the plan's static string, but the plan
      // still owns tone/confetti.
      const codedMessage = toBillingCommandErrorMessage(errorCode);
      emitFeedback(
        codedMessage
          ? { ...plan.feedback.error, message: codedMessage }
          : plan.feedback.error
      );
      releaseExecution();
    },
    [
      dispatch,
      emitAllAnalytics,
      emitFeedback,
      onTerminalFailure,
      releaseExecution,
    ]
  );

  const execute = useCallback(
    async (plan: PlannedBillingCommand): Promise<void> => {
      if (activeRef.current !== null || command.status !== "idle") {
        dispatch({ type: "command-started", orgId, plan });
        return;
      }

      const execution: ActiveExecution = {
        awaitedShopifyApprovalId: null,
        commandId: null,
        leftForShopifyApproval: false,
        plan,
      };
      activeRef.current = execution;
      dispatch({ type: "command-started", orgId, plan });
      emitAllAnalytics(plan.analytics.onStart);

      try {
        const response = await mutation.mutateAsync(plan.request);
        if (activeRef.current !== execution) {
          return;
        }

        execution.commandId = response.commandId;
        dispatch({ type: "command-accepted", commandId: response.commandId });
        emitAllAnalytics(plan.analytics.onSuccess);
        if (!plan.mayAwaitShopifyApproval && plan.feedback.success) {
          emitFeedback(plan.feedback.success);
        }

        clearTimer(invalidateTimerRef);
        invalidateTimerRef.current = setTimeout(() => {
          invalidateTimerRef.current = null;
          void invalidateSubscriptionAccess();
        }, plan.invalidateAfterMs);

        if (plan.approvalTimeoutMs !== null) {
          clearTimer(approvalTimerRef);
          approvalTimerRef.current = setTimeout(() => {
            approvalTimerRef.current = null;
            const current = activeRef.current;
            if (
              current !== execution ||
              current.commandId !== response.commandId
            ) {
              return;
            }
            dispatch({ type: "shopify-approval-timed-out" });
            if (plan.timeout) {
              emitAllAnalytics(plan.timeout.analytics);
              emitFeedback(plan.timeout.feedback);
            }
            releaseExecution();
          }, plan.approvalTimeoutMs);
        }
      } catch {
        if (activeRef.current !== execution) {
          return;
        }
        dispatch({ type: "mutation-failed" });
        failExecution(plan);
      }
    },
    [
      command.status,
      dispatch,
      emitAllAnalytics,
      emitFeedback,
      failExecution,
      invalidateSubscriptionAccess,
      mutation,
      orgId,
      releaseExecution,
    ]
  );

  useEffect(() => {
    if (executionOrgIdRef.current === orgId) {
      return;
    }
    executionOrgIdRef.current = orgId;
    releaseExecution();
  }, [orgId, releaseExecution]);

  useEffect(() => {
    const wasFetching = previousFetchingRef.current;
    previousFetchingRef.current = isRawSnapshotFetching;
    if (!wasFetching || isRawSnapshotFetching || rawSnapshot === null) {
      return;
    }

    const active = activeRef.current;
    if (!active || active.awaitedShopifyApprovalId !== null) {
      return;
    }

    const isConfirmed = isOptimisticEventConfirmed(
      rawSnapshot,
      active.plan.optimisticEvent
    );
    dispatch({ type: "raw-snapshot-observed", snapshot: rawSnapshot });
    if (isConfirmed) {
      releaseExecution();
    }
  }, [dispatch, isRawSnapshotFetching, rawSnapshot, releaseExecution]);

  useEffect(() => {
    const active = activeRef.current;
    // A re-commit can reuse an approval an earlier command requested, so the
    // approval's push is matched on the approval, not on the command.
    const isAwaitedShopifyApproval =
      active?.awaitedShopifyApprovalId != null &&
      websocketMessage?.type === "billingCommandUpdate" &&
      websocketMessage.checkpointType === "shopify_cap_approval_completed" &&
      websocketMessage.externalState?.shopifyApprovalId ===
        active.awaitedShopifyApprovalId;
    if (
      websocketMessage?.type !== "billingCommandUpdate" ||
      active?.commandId == null ||
      websocketMessage.orgId !== orgId ||
      (websocketMessage.billingCommandId !== active.commandId &&
        !isAwaitedShopifyApproval)
    ) {
      return;
    }

    // Each pushed message is acted on once. Without this the effect would
    // redirect, re-dispatch and re-arm the settle deadline on every render
    // that changes one of its dependencies, and the reconciling dispatch
    // would feed itself a fresh state object forever.
    if (handledMessageRef.current === websocketMessage) {
      return;
    }
    handledMessageRef.current = websocketMessage;

    const approvalUrl =
      websocketMessage.externalState?.shopifyApprovalConfirmationUrl;
    if (isAwaitedShopifyApproval) {
      if (approvalUrl) {
        leaveForShopifyApproval(active, approvalUrl);
      }
      return;
    }

    // A recorded plan mutated nothing and receives no further push of its own:
    // either it names the approval it waits on, whose own push carries the
    // link, or it owes nothing and is already the merchant's plan.
    if (
      active.plan.mayAwaitShopifyApproval &&
      websocketMessage.checkpointType === "execution_plan_ready" &&
      websocketMessage.status === "recorded"
    ) {
      if (approvalUrl) {
        leaveForShopifyApproval(active, approvalUrl);
        return;
      }
      const awaitedShopifyApprovalId =
        websocketMessage.externalState?.awaitedShopifyApprovalId;
      if (awaitedShopifyApprovalId) {
        active.awaitedShopifyApprovalId = awaitedShopifyApprovalId;
        dispatch({ type: "shopify-approval-named" });
        return;
      }
      if (active.plan.feedback.success) {
        emitFeedback(active.plan.feedback.success);
      }
      void invalidateSubscriptionAccess();
      dispatch({ type: "server-confirmed" });
      releaseExecution();
      return;
    }

    if (approvalUrl) {
      leaveForShopifyApproval(active, approvalUrl);
    }

    dispatch({
      type: "websocket-observed",
      message: {
        commandId: websocketMessage.billingCommandId,
        orgId: websocketMessage.orgId,
        status: websocketMessage.status,
      },
    });
    if (isTerminalCommandFailure(websocketMessage.status)) {
      failExecution(active.plan, websocketMessage.errorCode);
      return;
    }

    if (websocketMessage.status === "completed") {
      clearTimer(approvalTimerRef);
      // A plan that might have awaited an approval but never redirected was not
      // gated by one: the backend applied the change itself, so the success
      // toast held back at acceptance is owed to the merchant here.
      if (
        active.plan.mayAwaitShopifyApproval &&
        !active.leftForShopifyApproval &&
        active.plan.feedback.success
      ) {
        emitFeedback(active.plan.feedback.success);
      }

      // The command settled server-side, which may be well after the
      // fixed invalidateAfterMs timer already fired and caught stale data
      // (e.g. the command sat queued behind other org commands). Ask for a
      // fresh snapshot now so the isRawSnapshotFetching effect above can
      // confirm the optimistic overlay against real data; if the confirming
      // refetch still never observes it (lost websocket message, stale
      // cache), settle anyway after the deadline so the lock releases and
      // the merchant can act again. No feedback — nothing failed.
      void invalidateSubscriptionAccess();
      clearTimer(settleTimerRef);
      settleTimerRef.current = setTimeout(() => {
        settleTimerRef.current = null;
        if (activeRef.current !== active) {
          return;
        }
        dispatch({ type: "server-confirmed" });
        releaseExecution();
      }, SERVER_CONFIRM_DEADLINE_MS);
    }
  }, [
    reducerCommandId,
    dispatch,
    emitFeedback,
    failExecution,
    invalidateSubscriptionAccess,
    leaveForShopifyApproval,
    orgId,
    releaseExecution,
    websocketMessage,
  ]);

  useEffect(
    () => () => {
      releaseExecution();
    },
    [releaseExecution]
  );

  return { execute };
};
